import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Tabs,
  Table,
  Checkbox,
  Radio,
  Tag,
  Typography,
  message,
  Space,
  Empty,
  Divider,
} from 'antd';
import {
  SendOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  ApiOutlined,
  KeyOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

// Helper to generate unique IDs for table rows
const generateId = () => Math.random().toString(36).substring(2, 9);

const ApiTesting = () => {
  const { api, authToken } = useMyContext();

  // Request State
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  // Tabs State (Params, Headers, Body, Auth)
  const [queryParams, setQueryParams] = useState([
    { id: generateId(), key: '', value: '', enabled: true },
  ]);
  const [headers, setHeaders] = useState([
    { id: generateId(), key: '', value: '', enabled: true },
  ]);
  const [authType, setAuthType] = useState('none');
  const [bearerToken, setBearerToken] = useState('');
  const [basicUser, setBasicUser] = useState('');
  const [basicPass, setBasicPass] = useState('');

  // Body State
  const [bodyType, setBodyType] = useState('none');
  const [jsonBody, setJsonBody] = useState('');
  const [formData, setFormData] = useState([
    { id: generateId(), key: '', value: '', type: 'text', file: null, enabled: true },
  ]);
  const [urlEncoded, setUrlEncoded] = useState([
    { id: generateId(), key: '', value: '', enabled: true },
  ]);

  // Response State
  const [response, setResponse] = useState(null);

  // History State
  const [history, setHistory] = useState([]);

  // Load history and defaults on mount
  useEffect(() => {
    // Load request history
    try {
      const savedHistory = localStorage.getItem('api_tester_history');
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
    } catch (e) {
      console.error('Failed to load history', e);
    }

    // Default to the current API base path if available
    if (api) {
      setUrl(api);
    }
  }, [api]);

  // Save history helper
  const saveToHistory = (requestItem) => {
    const updated = [requestItem, ...history.filter(h => h.url !== requestItem.url || h.method !== requestItem.method)].slice(0, 15);
    setHistory(updated);
    localStorage.setItem('api_tester_history', JSON.stringify(updated));
  };

  // Clear history helper
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('api_tester_history');
    message.success('History cleared');
  };

  // Sync params table to URL
  useEffect(() => {
    if (!url) return;

    try {
      const parsedUrl = new URL(url.startsWith('http') ? url : `http://localhost/${url.replace(/^\//, '')}`);
      const searchParams = new URLSearchParams(parsedUrl.search);

      // Create parameters from URL that aren't already represented in state
      let updatedParams = [...queryParams];
      let changed = false;

      // Extract keys from current query parameters
      const currentKeys = queryParams.map(p => p.key).filter(Boolean);

      searchParams.forEach((val, key) => {
        if (!currentKeys.includes(key)) {
          // Add to query params
          if (updatedParams.length === 1 && !updatedParams[0].key) {
            updatedParams[0] = { id: generateId(), key, value: val, enabled: true };
          } else {
            updatedParams.push({ id: generateId(), key, value: val, enabled: true });
          }
          changed = true;
        }
      });

      if (changed) {
        setQueryParams(updatedParams);
      }
    } catch (err) {
      // Ignore invalid URL formats during typing
    }
  }, [url]);

  // Sync URL from params table
  const updateUrlFromParams = (paramsList) => {
    if (!url) return;
    try {
      // Base URL without query parameters
      const baseUrl = url.split('?')[0];
      const activeParams = paramsList.filter(p => p.enabled && p.key);

      if (activeParams.length === 0) {
        setUrl(baseUrl);
        return;
      }

      const searchParams = new URLSearchParams();
      activeParams.forEach(p => {
        searchParams.append(p.key, p.value);
      });

      setUrl(`${baseUrl}?${searchParams.toString()}`);
    } catch (err) {
      console.error(err);
    }
  };

  // Inject active session token
  const injectSessionToken = () => {
    if (!authToken) {
      message.warning('No active session token found.');
      return;
    }
    // Check if Authorization header already exists
    const hasAuth = headers.some(h => h.key.toLowerCase() === 'authorization');
    if (hasAuth) {
      setHeaders(headers.map(h =>
        h.key.toLowerCase() === 'authorization'
          ? { ...h, value: `Bearer ${authToken}`, enabled: true }
          : h
      ));
    } else {
      const newHeader = { id: generateId(), key: 'Authorization', value: `Bearer ${authToken}`, enabled: true };
      if (headers.length === 1 && !headers[0].key) {
        setHeaders([newHeader]);
      } else {
        setHeaders([...headers, newHeader]);
      }
    }
    message.success('Authorization Bearer Token injected from active session');
  };

  // Prettify JSON body
  const prettifyJson = () => {
    if (!jsonBody.trim()) return;
    try {
      const parsed = JSON.parse(jsonBody);
      setJsonBody(JSON.stringify(parsed, null, 2));
      message.success('JSON formatted successfully');
    } catch (err) {
      message.error(`Invalid JSON: ${err.message}`);
    }
  };

  // Build key-value table handlers
  const handleTableChange = (list, setList, id, field, val, syncToUrl = false) => {
    const updated = list.map(item => (item.id === id ? { ...item, [field]: val } : item));
    setList(updated);

    // Add empty row at end if user typed in the last row
    const lastRow = updated[updated.length - 1];
    if (lastRow && (lastRow.key || lastRow.value)) {
      setList([...updated, { id: generateId(), key: '', value: '', enabled: true }]);
    }

    if (syncToUrl) {
      updateUrlFromParams(updated);
    }
  };

  const handleFormDataTableChange = (list, setList, id, field, val) => {
    const updated = list.map(item => {
      if (item.id === id) {
        if (field === 'type') {
          return { ...item, type: val, value: '', file: null };
        }
        return { ...item, [field]: val };
      }
      return item;
    });
    setList(updated);

    const lastRow = updated[updated.length - 1];
    if (lastRow && (lastRow.key || lastRow.value || lastRow.file)) {
      setList([...updated, { id: generateId(), key: '', value: '', type: 'text', file: null, enabled: true }]);
    }
  };

  const handleDeleteRow = (list, setList, id, syncToUrl = false) => {
    if (list.length === 1) {
      // Don't delete last row, just clear it
      const cleared = [{ id: generateId(), key: '', value: '', enabled: true }];
      setList(cleared);
      if (syncToUrl) updateUrlFromParams(cleared);
      return;
    }
    const updated = list.filter(item => item.id !== id);
    setList(updated);
    if (syncToUrl) updateUrlFromParams(updated);
  };

  const handleDeleteFormDataRow = (list, setList, id) => {
    if (list.length === 1) {
      setList([{ id: generateId(), key: '', value: '', type: 'text', file: null, enabled: true }]);
      return;
    }
    setList(list.filter(item => item.id !== id));
  };

  // Load a request from history or preset
  const loadRequest = (req) => {
    setMethod(req.method || 'GET');
    setUrl(req.url || '');
    setAuthType(req.authType || 'none');
    setBearerToken(req.bearerToken || '');
    setBasicUser(req.basicUser || '');
    setBasicPass(req.basicPass || '');
    setBodyType(req.bodyType || 'none');
    setJsonBody(req.jsonBody || '');

    if (req.queryParams) setQueryParams(req.queryParams);
    if (req.headers) setHeaders(req.headers);
    if (req.formData) setFormData(req.formData);
    if (req.urlEncoded) setUrlEncoded(req.urlEncoded);

    message.success(`Loaded ${req.method} request`);
  };

  // Perform HTTP Request (executed server-side via the backend proxy)
  const handleSend = async () => {
    if (!url.trim()) {
      message.error('Please enter a request URL');
      return;
    }

    setLoading(true);
    setResponse(null);

    // Resolve URL: Prepend system base API URL if it's a relative path
    let requestUrl = url.trim();
    if (requestUrl.startsWith('/')) {
      if (api) {
        requestUrl = `${api.replace(/\/$/, '')}${requestUrl}`;
      } else {
        requestUrl = `${window.location.origin}${requestUrl}`;
      }
    }

    // Assemble Headers
    const reqHeaders = {};
    headers.forEach(h => {
      if (h.enabled && h.key.trim()) {
        reqHeaders[h.key.trim()] = h.value;
      }
    });

    // Handle Authentication Configuration (merged into headers for the backend)
    if (authType === 'bearer' && bearerToken) {
      reqHeaders['Authorization'] = `Bearer ${bearerToken}`;
    } else if (authType === 'basic' && (basicUser || basicPass)) {
      const encoded = btoa(`${basicUser}:${basicPass}`);
      reqHeaders['Authorization'] = `Basic ${encoded}`;
    }

    // Does this form-data request include any files to upload?
    const hasFiles =
      bodyType === 'form-data' &&
      formData.some(f => f.enabled && f.type === 'file' && f.file && f.key.trim());

    const startTime = performance.now();

    try {
      let res;

      if (hasFiles) {
        // Send everything as multipart so the backend can forward the files.
        const fd = new FormData();
        fd.append('method', method);
        fd.append('url', requestUrl);
        fd.append('headers', JSON.stringify(reqHeaders));
        fd.append('body_type', 'form-data');

        const textRows = formData
          .filter(f => f.enabled && f.type === 'text' && f.key.trim())
          .map(f => ({ key: f.key.trim(), value: f.value }));
        fd.append('form_data', JSON.stringify(textRows));

        formData.forEach(f => {
          if (f.enabled && f.type === 'file' && f.file && f.key.trim()) {
            fd.append(`files[${f.key.trim()}]`, f.file);
          }
        });

        res = await apiClient.post('api-tester/execute', fd);
      } else {
        // Describe the request as JSON; the backend executes it.
        const payload = {
          method,
          url: requestUrl,
          headers: reqHeaders,
          body_type: bodyType,
        };

        if (method !== 'GET') {
          if (bodyType === 'json') {
            payload.json_body = jsonBody;
          } else if (bodyType === 'x-www-form-urlencoded') {
            payload.url_encoded = urlEncoded
              .filter(p => p.enabled && p.key.trim())
              .map(p => ({ key: p.key.trim(), value: p.value }));
          } else if (bodyType === 'form-data') {
            payload.form_data = formData
              .filter(f => f.enabled && f.key.trim())
              .map(f => ({ key: f.key.trim(), value: f.value }));
          }
        }

        res = await apiClient.post('api-tester/execute', payload);
      }

      const result = res.data?.result;
      if (!result) {
        throw new Error('Malformed response received from server.');
      }

      setResponse(result);

      // Save to Request History
      saveToHistory({
        method,
        url,
        authType,
        bearerToken,
        basicUser,
        basicPass,
        bodyType,
        jsonBody,
        queryParams: queryParams.filter(q => q.key),
        headers: headers.filter(h => h.key),
        formData: formData.filter(f => f.key || f.file),
        urlEncoded: urlEncoded.filter(u => u.key),
        timestamp: new Date().toLocaleTimeString(),
      });

      if (result.status === 'Error') {
        message.error(`Request failed: ${result.statusText}`);
      } else if (typeof result.status === 'number' && result.status >= 400) {
        message.warning(`Request finished with status code ${result.status}`);
      } else {
        message.success(`Request completed successfully in ${result.time}ms`);
      }
    } catch (err) {
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      const serverError = err.response?.data;

      setResponse({
        status: err.response?.status || 'Error',
        statusText: serverError?.error || err.message || 'Request Failed',
        time: responseTime,
        size: '0 B',
        headers: {},
        data: serverError || err.stack || 'The API tester backend is unreachable.',
        isJson: typeof serverError === 'object',
      });

      message.error(`Request failed: ${serverError?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format bytes helper
  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Clipboard copy helper
  const copyToClipboard = (text) => {
    const content = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text);
    navigator.clipboard.writeText(content)
      .then(() => message.success('Copied response to clipboard'))
      .catch(() => message.error('Failed to copy text'));
  };

  // Download response helper
  const downloadResponse = (data, status) => {
    try {
      const content = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `response_${status || 'data'}_${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('Response downloaded');
    } catch (e) {
      message.error('Failed to download response');
    }
  };

  // Get status color tag
  const getStatusColor = (status) => {
    if (typeof status !== 'number') return 'red';
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'blue';
    if (status >= 400 && status < 500) return 'warning';
    return 'error';
  };

  // Preset Endpoints
  const presets = [
    { name: 'App Config / Logs', method: 'GET', url: '/log' },
    { name: 'Keyword Restriction', method: 'GET', url: '/keyword-restriction' },
    { name: 'Users List', method: 'GET', url: '/users' },
    { name: 'System Settings', method: 'GET', url: '/setting' },
    { name: 'API Key Management', method: 'GET', url: '/manage-api-keys' },
    { name: 'Meta Pricing Table', method: 'GET', url: '/meta-pricing' },
  ];

  // Request Tab Configuration
  const requestTabItems = [
    {
      key: 'params',
      label: 'Params (Query)',
      children: (
        <Table
          dataSource={queryParams}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: '',
              dataIndex: 'enabled',
              width: 50,
              render: (checked, record) => (
                <Checkbox
                  checked={checked}
                  onChange={(e) => handleTableChange(queryParams, setQueryParams, record.id, 'enabled', e.target.checked, true)}
                />
              ),
            },
            {
              title: 'Key',
              dataIndex: 'key',
              render: (text, record) => (
                <Input
                  placeholder="Parameter Key"
                  value={text}
                  bordered={false}
                  onChange={(e) => handleTableChange(queryParams, setQueryParams, record.id, 'key', e.target.value, true)}
                />
              ),
            },
            {
              title: 'Value',
              dataIndex: 'value',
              render: (text, record) => (
                <Input
                  placeholder="Parameter Value"
                  value={text}
                  bordered={false}
                  onChange={(e) => handleTableChange(queryParams, setQueryParams, record.id, 'value', e.target.value, true)}
                />
              ),
            },
            {
              title: '',
              key: 'action',
              width: 50,
              render: (_, record) => (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteRow(queryParams, setQueryParams, record.id, true)}
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      key: 'headers',
      label: 'Headers',
      children: (
        <div>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="dashed"
              icon={<KeyOutlined />}
              size="small"
              onClick={injectSessionToken}
            >
              Use Current Session Token
            </Button>
          </div>
          <Table
            dataSource={headers}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: '',
                dataIndex: 'enabled',
                width: 50,
                render: (checked, record) => (
                  <Checkbox
                    checked={checked}
                    onChange={(e) => handleTableChange(headers, setHeaders, record.id, 'enabled', e.target.checked)}
                  />
                ),
              },
              {
                title: 'Header Key',
                dataIndex: 'key',
                render: (text, record) => (
                  <Input
                    placeholder="e.g. Content-Type"
                    value={text}
                    bordered={false}
                    onChange={(e) => handleTableChange(headers, setHeaders, record.id, 'key', e.target.value)}
                  />
                ),
              },
              {
                title: 'Value',
                dataIndex: 'value',
                render: (text, record) => (
                  <Input
                    placeholder="Value"
                    value={text}
                    bordered={false}
                    onChange={(e) => handleTableChange(headers, setHeaders, record.id, 'value', e.target.value)}
                  />
                ),
              },
              {
                title: '',
                key: 'action',
                width: 50,
                render: (_, record) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRow(headers, setHeaders, record.id)}
                  />
                ),
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'body',
      label: 'Body',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Radio.Group value={bodyType} onChange={(e) => setBodyType(e.target.value)}>
              <Radio value="none">none</Radio>
              <Radio value="json">JSON</Radio>
              <Radio value="form-data">form-data</Radio>
              <Radio value="x-www-form-urlencoded">x-www-form-urlencoded</Radio>
            </Radio.Group>
          </div>

          {bodyType === 'none' && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#8c8c8c' }}>
              This request does not have a body.
            </div>
          )}

          {bodyType === 'json' && (
            <div>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" icon={<CodeOutlined />} onClick={prettifyJson}>
                  Prettify JSON
                </Button>
              </div>
              <Input.TextArea
                rows={8}
                value={jsonBody}
                onChange={(e) => setJsonBody(e.target.value)}
                placeholder="{\n  &quot;key&quot;: &quot;value&quot;\n}"
                style={{ fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#fafafa' }}
              />
            </div>
          )}

          {bodyType === 'form-data' && (
            <Table
              dataSource={formData}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '',
                  dataIndex: 'enabled',
                  width: 50,
                  render: (checked, record) => (
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleFormDataTableChange(formData, setFormData, record.id, 'enabled', e.target.checked)}
                    />
                  ),
                },
                {
                  title: 'Key',
                  dataIndex: 'key',
                  render: (text, record) => (
                    <Input
                      placeholder="Key"
                      value={text}
                      bordered={false}
                      onChange={(e) => handleFormDataTableChange(formData, setFormData, record.id, 'key', e.target.value)}
                    />
                  ),
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  width: 100,
                  render: (type, record) => (
                    <Select
                      value={type}
                      bordered={false}
                      onChange={(val) => handleFormDataTableChange(formData, setFormData, record.id, 'type', val)}
                    >
                      <Option value="text">Text</Option>
                      <Option value="file">File</Option>
                    </Select>
                  ),
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  render: (text, record) => {
                    if (record.type === 'file') {
                      return (
                        <div style={{ padding: '4px 11px' }}>
                          <input
                            type="file"
                            onChange={(e) => {
                              const fileObj = e.target.files[0];
                              handleFormDataTableChange(formData, setFormData, record.id, 'file', fileObj);
                            }}
                          />
                          {record.file && <Text type="secondary" style={{ marginLeft: 8 }}>({formatSize(record.file.size)})</Text>}
                        </div>
                      );
                    }
                    return (
                      <Input
                        placeholder="Value"
                        value={text}
                        bordered={false}
                        onChange={(e) => handleFormDataTableChange(formData, setFormData, record.id, 'value', e.target.value)}
                      />
                    );
                  },
                },
                {
                  title: '',
                  key: 'action',
                  width: 50,
                  render: (_, record) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFormDataRow(formData, setFormData, record.id)}
                    />
                  ),
                },
              ]}
            />
          )}

          {bodyType === 'x-www-form-urlencoded' && (
            <Table
              dataSource={urlEncoded}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '',
                  dataIndex: 'enabled',
                  width: 50,
                  render: (checked, record) => (
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleTableChange(urlEncoded, setUrlEncoded, record.id, 'enabled', e.target.checked)}
                    />
                  ),
                },
                {
                  title: 'Key',
                  dataIndex: 'key',
                  render: (text, record) => (
                    <Input
                      placeholder="Key"
                      value={text}
                      bordered={false}
                      onChange={(e) => handleTableChange(urlEncoded, setUrlEncoded, record.id, 'key', e.target.value)}
                    />
                  ),
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  render: (text, record) => (
                    <Input
                      placeholder="Value"
                      value={text}
                      bordered={false}
                      onChange={(e) => handleTableChange(urlEncoded, setUrlEncoded, record.id, 'value', e.target.value)}
                    />
                  ),
                },
                {
                  title: '',
                  key: 'action',
                  width: 50,
                  render: (_, record) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteRow(urlEncoded, setUrlEncoded, record.id)}
                    />
                  ),
                },
              ]}
            />
          )}
        </div>
      ),
    },
    {
      key: 'auth',
      label: 'Authorization',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 12 }}>Auth Type:</span>
            <Select value={authType} style={{ width: 200 }} onChange={(val) => setAuthType(val)}>
              <Option value="none">No Auth</Option>
              <Option value="bearer">Bearer Token</Option>
              <Option value="basic">Basic Auth</Option>
            </Select>
          </div>

          {authType === 'none' && (
            <div style={{ padding: '16px 0', color: '#8c8c8c' }}>
              This request doesn't send authorization details.
            </div>
          )}

          {authType === 'bearer' && (
            <div style={{ maxWidth: 600 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Token</Text></div>
              <Input.TextArea
                rows={3}
                placeholder="Paste Bearer Token here..."
                value={bearerToken}
                onChange={(e) => setBearerToken(e.target.value)}
              />
            </div>
          )}

          {authType === 'basic' && (
            <div style={{ maxWidth: 400 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}><Text strong>Username</Text></div>
                <Input
                  placeholder="Username"
                  value={basicUser}
                  onChange={(e) => setBasicUser(e.target.value)}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4 }}><Text strong>Password</Text></div>
                <Input.Password
                  placeholder="Password"
                  value={basicPass}
                  onChange={(e) => setBasicPass(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      <Row gutter={[16, 16]}>

        {/* Right Panel: URL, Method, Request Setup, Response */}
        <Col xs={24} md={24}>
          <Card
            title={
              <Space>
                <ApiOutlined />
                <span>API Tester</span>
              </Space>
            }
          >
            {/* Input Bar */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <Select
                value={method}
                onChange={(val) => setMethod(val)}
                style={{ width: '120px' }}
                size="large"
              >
                <Option value="GET"><span style={{ color: 'green', fontWeight: 'bold' }}>GET</span></Option>
                <Option value="POST"><span style={{ color: '#d46b08', fontWeight: 'bold' }}>POST</span></Option>
                <Option value="PUT"><span style={{ color: '#096dd9', fontWeight: 'bold' }}>PUT</span></Option>
                <Option value="DELETE"><span style={{ color: '#cf1322', fontWeight: 'bold' }}>DELETE</span></Option>
                <Option value="PATCH"><span style={{ color: '#722ed1', fontWeight: 'bold' }}>PATCH</span></Option>
              </Select>
              <Input
                placeholder="Enter request URL (e.g. /users or https://api.example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                style={{ flex: 1, fontFamily: 'monospace' }}
                size="large"
                onPressEnter={handleSend}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={loading}
                size="large"
                style={{ width: '110px' }}
              >
                Send
              </Button>
            </div>

            {/* Request Settings Tab */}
            <Tabs defaultActiveKey="params" items={requestTabItems} style={{ marginBottom: 24 }} />

            {/* Response Section */}
            <Divider style={{ margin: '16px 0' }} />
            <Title level={4} style={{ marginBottom: 16 }}>Response</Title>

            {loading && (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <Empty description="Sending request..." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )}

            {!loading && !response && (
              <Empty description="Send a request to inspect the response details" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}

            {!loading && response && (
              <div>
                {/* Response Metadata Bar */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                  <Space>
                    <Text strong>Status:</Text>
                    <Tag color={getStatusColor(response.status)} style={{ fontSize: '13px', padding: '2px 8px' }}>
                      {response.status} {response.statusText}
                    </Tag>
                  </Space>
                  <Space>
                    <Text strong>Time:</Text>
                    <Tag color="cyan" style={{ fontSize: '13px', padding: '2px 8px' }}>
                      {response.time} ms
                    </Tag>
                  </Space>
                  <Space>
                    <Text strong>Size:</Text>
                    <Tag color="blue" style={{ fontSize: '13px', padding: '2px 8px' }}>
                      {response.size}
                    </Tag>
                  </Space>
                </div>

                {/* Response Content Tabs */}
                <Tabs
                  defaultActiveKey="body"
                  items={[
                    {
                      key: 'body',
                      label: 'Response Body',
                      children: (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: 8 }}>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(response.data)}
                            >
                              Copy Body
                            </Button>
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => downloadResponse(response.data, response.status)}
                            >
                              Download JSON
                            </Button>
                          </div>
                          <pre
                            style={{
                              backgroundColor: '#1e1e1e',
                              color: '#d4d4d4',
                              padding: '16px',
                              borderRadius: '6px',
                              overflowX: 'auto',
                              maxHeight: '400px',
                              fontSize: '13px',
                              fontFamily: 'monospace',
                              border: '1px solid #333',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all'
                            }}
                          >
                            {response.isJson
                              ? JSON.stringify(response.data, null, 2)
                              : typeof response.data === 'string'
                                ? response.data
                                : JSON.stringify(response.data)}
                          </pre>
                        </div>
                      ),
                    },
                    {
                      key: 'headers',
                      label: 'Response Headers',
                      children: (
                        <Table
                          dataSource={Object.keys(response.headers).map(key => ({
                            key,
                            name: key,
                            value: response.headers[key]
                          }))}
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Header Name',
                              dataIndex: 'name',
                              key: 'name',
                              width: '30%',
                              render: (text) => <Text strong>{text}</Text>
                            },
                            {
                              title: 'Value',
                              dataIndex: 'value',
                              key: 'value',
                              render: (text) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>
                            }
                          ]}
                        />
                      ),
                    }
                  ]}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ApiTesting;
